<template>
  <div
    v-if="userStore.language"
    class="pb-home"
  >
    <ProfileList
      class="pb-double-margin-bottom"
      allow-empty
      show-filters
      show-search
      show-refresh
      column-brand
      column-owner
      column-updated
      column-www
      column-official
      link
    >
      <template #header="{ trashFilterEnabled }">
        <TableCell
          class="pb-column-header pb-profiles-list-column-icons"
          head
          center
        >
          <UiIcon
            v-if="trashFilterEnabled"
            v-tooltip="{ text: translate('generic.trash') }"
            icon="trash"
            color="muted"
            class="pb-icon-cell-item"
          />
          <UiIcon
            v-else
            v-tooltip="{ text: translate('pb2.downloadProfile.label') }"
            icon="download"
            color="muted"
            class="pb-icon-cell-item"
          />
        </TableCell>
      </template>

      <template #default="{ item }">
        <TableCell
          v-if="item.isRemove"
          clickable
          class="pb-profiles-list-column-icons pb-icon-cell"
          center
          @select="askRestoreConfirmation(item)"
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.restoreProfile.label') }"
            icon="restore"
            color="muted"
            class="pb-icon-cell-item"
          />
        </TableCell>
        <ProfileListDownloadCell
          v-else
          :item
        />
      </template>

      <template #no-result>
        <h2>{{ translate('pb2.homeView.noProfileFound') }}</h2>
        <BaseButton
          v-if="noResultButton"
          v-bind="noResultButton.props"
          class="txt-info hover-black focus-black txt-w-semibold pb-half-margin-top"
        >
          {{ noResultButton.label }}
        </BaseButton>
      </template>
    </ProfileList>

    <RequestsList class="pb-double-margin-top flex-grow">
      <div>
        <h2>{{ translate('pb2.homeView.requestSection.title') }}</h2>
        <p class="pb-quarter-margin-top">
          {{ translate('pb2.homeView.requestSection.message') }}
          <BaseButton
            class="txt-info hover-black focus-black txt-w-semibold"
            :to="{
              name: routeNames.Request,
              query: profileQuery,
            }"
          >
            {{ translate('pb2.homeView.requestSection.sendRequest') }}
          </BaseButton>
          {{ translate('pb2.homeView.requestSection.alternativeMessage') }}
          <BaseButton
            class="txt-info hover-black focus-black txt-w-semibold"
            :to="{
              name: routeNames.Create,
              query: profileQuery,
            }"
          >
            {{ translate('pb2.homeView.requestSection.createYourself') }}
          </BaseButton>
        </p>
      </div>
    </RequestsList>

    <BaseButton
      tooltip="Show release notes"
      class="pb-home-release-version"
      @click="openReleaseNotes"
    >
      v{{ version }}
    </BaseButton>
  </div>
  <ConfirmDialog
    v-bind="restoreConfirmControl"
    confirm-style="success"
    confirm-text="pb2.restoreProfile.confirmLabel"
    :dialog-title="translate('pb2.restoreProfile.confirmTitle')"
  >
    {{ translate('pb2.restoreProfile.confirmMessage') }} "{{ toRestore?.name }}" ?
  </ConfirmDialog>
</template>

<script setup>
import {
  ref,
  computed,
} from 'vue'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import useCloseOthers from '@nicolaudie-group/ng-ui-kit/composables/useCloseOthers'
import useConfirmDialog from '@nicolaudie-group/ng-ui-kit/composables/useConfirmDialog'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import TableCell from '@nicolaudie-group/ng-ui-kit/components/Table/TableCell.vue'
import useTranslate from '@/composables/useTranslate'
// import useSmartSettings from '@/composables/useSmartSettings'
// import useViewMode from '@/composables/useViewMode'
import useUserStore from '@/stores/user.store'
import useProfileStore from '@/stores/profile.store'
import useRouterStore from '@/stores/router.store'
// import TableButton from '@/components/TableButton.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ProfileList from '@/components/ProfileList/ProfileList.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import useReleaseNotes from '@/composables/useReleaseNotes'

import routeNames from '@/router/names'
import useProfileList from '@/composables/useProfileList'
import ProfileListDownloadCell from '@/components/ProfileList/ProfileListDownloadCell.vue'
import RequestsList from '@/components/RequestsList/RequestsList.vue'

const userStore = useUserStore()
const profileStore = useProfileStore()
const routerStore = useRouterStore()

// const { translate, translationsLoaded } = useTranslate()
const { translate } = useTranslate()

const toRestore = ref(null)

async function restoreAndOpenProfile() {
  const { uid, link } = toRestore.value
  await profileStore.restore(uid)
  return routerStore.push(link)
}

const {
  search,
  filter,
} = useProfileList({
  filterQuery: true,
  paginationQuery: true,
  sortQuery: true,
  searchQuery: true,
})

const profileQuery = computed(() => ({
  brand: search.brand.value || undefined,
  name: search.name.value || undefined,
}))

const {
  filterProps,
  officialFilterEnabled,
  mineFilterEnabled,
  allFilterEnabled,
  trashFilterEnabled,
} = filter

const noResultButton = computed(() => {
  switch (true) {
    case officialFilterEnabled.value:
    case trashFilterEnabled.value:
      return {
        props: filterProps.value.mine,
        label: translate('pb2.homeView.searchMineLabel'),
      }
    case mineFilterEnabled.value:
    case allFilterEnabled.value:
      return {
        props: filterProps.value.official,
        label: translate('pb2.homeView.searchOfficialLabel'),
      }
    default:
      return null
  }
})

const {
  control: restoreConfirmControl,
  open: openRestoreConfirm,
  cancel: cancelRestoreConfirm,
  state: restoreConfirmState,
} = useConfirmDialog(restoreAndOpenProfile)

useCloseOthers(cancelRestoreConfirm, restoreConfirmState)

function askRestoreConfirmation(item) {
  toRestore.value = item
  return openRestoreConfirm()
}

const { openDialog: openReleaseNotes, version } = useReleaseNotes()
</script>

<style lang="scss" src="./HomeView.scss" />
